"use client";

import { useState, useEffect, Fragment } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Dialog, Transition } from "@headlessui/react";
import { FiSearch, FiShoppingBag, FiUser, FiMenu } from "react-icons/fi";
import { useMiniCart } from "@/app/context/MiniCartContext";
import AuthModal from "../AuthModal";

/* ================= TYPES ================= */

interface NavLink {
  id: number;
  url: string;
  text: string;
}

interface Props {
  links: NavLink[];
  logoUrl: string | null;
  logoText: string | null;
}

/* ================= COMPONENT ================= */

export default function Navbar_Layout_CenterFocus({
  links,
  logoUrl,
  logoText,
}: Props) {
  const pathname = usePathname();
  const { open, itemCount } = useMiniCart();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  /* ================= SCROLL ================= */

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* ================= ACTIVE ROUTE ================= */

  const isActiveRoute = (href: string) => {
    if (href === "/") return pathname === "/";
    return pathname === href || pathname.startsWith(href + "/");
  };

  const isCartActive =
    pathname.startsWith("/cart") || pathname.startsWith("/checkout");

  const isAccountActive =
    pathname.startsWith("/account") || pathname.startsWith("/profile");

  /* ================= SPLIT LINKS ================= */

  const mid = Math.ceil(links.length / 2);
  const leftLinks = links.slice(0, mid);
  const rightLinks = links.slice(mid);

  /* ================= RENDER ================= */

  return (
    <>
      {/* ================= HEADER ================= */}
      <header
        className={`
          fixed top-0 left-0 right-0 z-50
          transition-all duration-300
          ${
            scrolled
              ? "bg-[var(--nav-bg)]/95 backdrop-blur-md border-b border-[var(--border-1)] shadow-[var(--shadow-sm)]"
              : "bg-[var(--nav-bg)] border-b border-[var(--border-1)]"
          }
        `}
      >
        <nav className="max-w-7xl mx-auto h-16 px-6 flex items-center justify-between">
          {/* ================= LEFT LINKS ================= */}
          <div className="hidden lg:flex items-center gap-8">
            {leftLinks.map((link) => {
              const active = isActiveRoute(link.url);

              return (
                <Link
                  key={link.id}
                  href={link.url}
                  className="relative group px-1 py-2"
                >
                  <span
                    className={`
                      text-sm tracking-wide transition
                      ${
                        active
                          ? "text-[var(--text-1)] font-medium"
                          : "text-[var(--text-muted)] group-hover:text-[var(--text-1)]"
                      }
                    `}
                  >
                    {link.text}
                  </span>

                  {/* Center Focus underline */}
                  <span
                    className={`
                      absolute left-1/2 -bottom-2 h-px
                      -translate-x-1/2 transition-all duration-300
                      ${
                        active
                          ? "w-8 bg-[var(--secondary)]"
                          : "w-0 bg-transparent group-hover:w-6 group-hover:bg-[var(--secondary)]"
                      }
                    `}
                  />
                </Link>
              );
            })}
          </div>

          {/* ================= CENTER LOGO ================= */}
          <Link
            href="/"
            className="flex flex-col items-center justify-center group"
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={logoText || "Logo"}
                className="h-8 transition-opacity group-hover:opacity-90"
              />
            ) : (
              <span className="text-xl font-medium tracking-wide text-[var(--title)]">
                {logoText || "Brand"}
              </span>
            )}
            <span className="mt-1 h-px w-8 bg-[var(--secondary)] opacity-60 group-hover:opacity-100 transition" />
          </Link>

          {/* ================= RIGHT LINKS + ACTIONS ================= */}
          <div className="hidden lg:flex items-center gap-8">
            {rightLinks.map((link) => {
              const active = isActiveRoute(link.url);

              return (
                <Link
                  key={link.id}
                  href={link.url}
                  className="relative group px-1 py-2"
                >
                  <span
                    className={`
                      text-sm tracking-wide transition
                      ${
                        active
                          ? "text-[var(--text-1)] font-medium"
                          : "text-[var(--text-muted)] group-hover:text-[var(--text-1)]"
                      }
                    `}
                  >
                    {link.text}
                  </span>

                  <span
                    className={`
                      absolute left-1/2 -bottom-2 h-px
                      -translate-x-1/2 transition-all duration-300
                      ${
                        active
                          ? "w-8 bg-[var(--secondary)]"
                          : "w-0 bg-transparent group-hover:w-6 group-hover:bg-[var(--secondary)]"
                      }
                    `}
                  />
                </Link>
              );
            })}

            {/* ================= ACTION ICONS ================= */}
            <div className="flex items-center gap-3 pl-6 border-l border-[var(--border-1)]">
              <button className="p-2 rounded-md text-[var(--text-muted)] hover:text-[var(--text-1)] hover:bg-[var(--accent-1)] transition">
                <FiSearch size={16} />
              </button>

              <button
                onClick={open}
                className={`
                  relative p-2 rounded-md transition
                  ${
                    isCartActive
                      ? "text-[var(--secondary)] bg-[var(--accent-1)]"
                      : "text-[var(--text-muted)] hover:text-[var(--text-1)] hover:bg-[var(--accent-1)]"
                  }
                `}
              >
                <FiShoppingBag size={16} />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-[var(--secondary)] text-[var(--text-inverse)] text-xs flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </button>

              <button
                onClick={() => setAuthOpen(true)}
                className={`
                  p-2 rounded-md transition
                  ${
                    isAccountActive
                      ? "text-[var(--secondary)] bg-[var(--accent-1)]"
                      : "text-[var(--text-muted)] hover:text-[var(--text-1)] hover:bg-[var(--accent-1)]"
                  }
                `}
              >
                <FiUser size={16} />
              </button>
            </div>
          </div>

          {/* ================= MOBILE ================= */}
          <div className="lg:hidden flex items-center gap-2">
            <button
              onClick={open}
              className="relative p-2 text-[var(--text-muted)]"
            >
              <FiShoppingBag size={18} />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-[var(--secondary)] text-[var(--text-inverse)] text-xs flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </button>

            <button
              onClick={() => setMobileOpen(true)}
              className="p-2 text-[var(--text-muted)]"
            >
              <FiMenu size={18} />
            </button>
          </div>
        </nav>
      </header>

      {/* ================= SPACER ================= */}
      <div className="h-16" />

      {/* ================= MOBILE DRAWER ================= */}
      <Transition show={mobileOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={setMobileOpen}>
          <div className="fixed inset-0 bg-black/40" />
          <Dialog.Panel className="fixed right-0 top-0 h-full w-72 bg-[var(--bg-col-1)] border-l border-[var(--border-1)] shadow-[var(--shadow-lg)] p-6">
            <div className="flex flex-col gap-4">
              {links.map((link) => {
                const active = isActiveRoute(link.url);
                return (
                  <Link
                    key={link.id}
                    href={link.url}
                    onClick={() => setMobileOpen(false)}
                    className={`
                      flex items-center gap-3 py-2 px-3 rounded-md text-sm transition
                      ${
                        active
                          ? "text-[var(--text-1)] bg-[var(--accent-1)] font-medium"
                          : "text-[var(--text-muted)] hover:text-[var(--text-1)]"
                      }
                    `}
                  >
                    <span
                      className={`
                        w-1.5 h-1.5 rounded-full
                        ${active ? "bg-[var(--secondary)]" : "bg-transparent"}
                      `}
                    />
                    {link.text}
                  </Link>
                );
              })}

              <button
                onClick={() => {
                  setMobileOpen(false);
                  setAuthOpen(true);
                }}
                className="mt-6 py-2 rounded-md bg-[var(--btn-bg-2)] text-[var(--btn-text-2)]"
              >
                Account
              </button>
            </div>
          </Dialog.Panel>
        </Dialog>
      </Transition>

      {/* ================= AUTH ================= */}
      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        onLoginSuccess={() => setAuthOpen(false)}
      />
    </>
  );
}
